import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import {
  theme,
  IconInformation,
  IconPrimaryApp
} from '@contrast-security-inc/design-system-foundations'
import {
  Tooltip,
  Popover,
  usePopover,
  UsePopoverProps
} from '@contrast-security-inc/design-system-popovers'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { ExpandableList } from 'contrastBase/layout'

export const FrameworkItem = ({ label }) => (
  <Text as="div" font="body">
    {label}
  </Text>
)
// exported for test only
export const AppOverviewFrameworksPopover = ({
  isOpen,
  isMerged,
  listItems,
  popoverProps
}: {
  isOpen: boolean
  isMerged: boolean
  listItems: any[]
  popoverProps: UsePopoverProps
}) => {
  const intl = useIntl()

  return (
    <Popover isOpen={isOpen} style={{ padding: '24px' }} {...popoverProps}>
      <Stack>
        {isMerged && (
          <IconPrimaryApp
            testId="app-overview-frameworks-popover-primary-icon"
            color="black"
          />
        )}
        <Text
          font="bodyStrong"
          className={css`
            display: inline-block;
          `}
        >
          {intl.formatMessage({ id: 'title_supportedFrameworks' })}
        </Text>
      </Stack>
      {isMerged && (
        <Text
          font="body"
          className={css`
            padding: 12px 0px;
            max-width: 200px;
            display: inline-block;
          `}
        >
          {intl.formatMessage({ id: 'detail_supportedFrameworksPrimaryApp' })}
        </Text>
      )}
      <ExpandableList listItems={listItems} ItemComponent={FrameworkItem} />
    </Popover>
  )
}

const AppOverviewFrameworksNotDetectedPopover = ({
  isOpen,
  isEnabled,
  language,
  popoverProps
}: {
  isOpen: boolean
  isEnabled: boolean
  language: string
  popoverProps: UsePopoverProps
}) => {
  const intl = useIntl()
  const isLanguageSupported =
    language === 'Java' ||
    language === '.NET' ||
    language === '.NET Core' ||
    language === 'Python'

  return (
    <Popover isOpen={isOpen} style={{ padding: '24px' }} {...popoverProps}>
      <Stack>
        <Text
          font="bodyStrong"
          className={css`
            display: inline-block;
            marginbottom: 8px;
          `}
        >
          {intl.formatMessage({ id: 'title_noFrameworksDetected' })}
        </Text>
      </Stack>
      <Text
        font="body"
        className={css`
          padding: 12px 0px;
          max-width: 300px;
          display: inline-block;
        `}
      >
        {intl.formatMessage(
          {
            id: isEnabled
              ? 'detail_noFrameworksDetected'
              : isLanguageSupported
              ? 'detail_frameworksNotSupportedAgentVersion'
              : 'detail_frameworksNotSupported'
          },
          { language }
        )}
      </Text>
    </Popover>
  )
}

const AppOverviewFrameworksNotDetected = ({
  isEnabled,
  language
}: {
  isEnabled: boolean
  language: string
}) => {
  const intl = useIntl()
  const { popoverTriggerProps, popoverProps } = usePopover({
    hasArrow: true,
    placement: 'bottom'
  })
  const [isOpen, setIsOpen] = React.useState(false)
  const onNaClick = React.useCallback(() => {
    setIsOpen((state) => !state)
  }, [setIsOpen])

  return (
    <div
      className={css`
        min-height: 60px;
      `}
    >
      <Link onClick={onNaClick} font="metadata" {...popoverTriggerProps}>
        {intl.formatMessage({ id: 'placeholder_na' })}
      </Link>
      <AppOverviewFrameworksNotDetectedPopover
        language={language}
        isOpen={isOpen}
        isEnabled={isEnabled}
        popoverProps={popoverProps}
      />
    </div>
  )
}

const minimalSizeCss = css``
const fullSizeCss = css`
  padding-left: 80px;
  border-left: 2px ${theme.color.neutral30} solid;
`
export const AppOverviewFrameworks = ({
  isEnabled,
  isMerged,
  language,
  frameworks = [],
  size = 'minimal'
}: {
  isEnabled: boolean
  isMerged: boolean
  language: string
  frameworks?: string[]
  size?: string
}) => {
  const intl = useIntl()
  const listItems = frameworks?.map((it) => ({ label: it }))
  const { popoverTriggerProps, popoverProps } = usePopover({
    hasArrow: true,
    placement: 'bottom'
  })
  const [isOpen, setIsOpen] = React.useState(false)
  const onViewAllClick = React.useCallback(() => {
    setIsOpen((state) => !state)
  }, [setIsOpen])

  return (
    <Stack
      flexDirection="column"
      spacing="0px"
      className={size === 'full' ? fullSizeCss : minimalSizeCss}
    >
      <Stack alignItems="center" spacing="4px">
        <Text font="bodyStrong">
          {intl.formatMessage({ id: 'title_routingFrameworks' })}
        </Text>
        <Tooltip
          style={{ maxWidth: '280px' }}
          body={
            intl.formatMessage({ id: 'detail_routingFrameworksTooltip' }) +
            (isMerged
              ? ` ${intl.formatMessage({
                  id: 'detail_routingFrameworksTooltipPrimary'
                })}`
              : '')
          }
        >
          <IconInformation
            size="sm"
            color="neutral60"
            testId="app-overview-frameworks-tooltip-icon"
          />
        </Tooltip>
      </Stack>
      {listItems.length === 0 ? (
        <AppOverviewFrameworksNotDetected
          isEnabled={isEnabled}
          language={language}
        />
      ) : (
        <>
          <ExpandableList
            viewAllLinkProps={{
              onClick: onViewAllClick,
              font: 'metadata',
              ...popoverTriggerProps
            }}
            defaultItemsDisplayed={3}
            isLoading={!frameworks}
            listItems={listItems}
            ItemComponent={FrameworkItem}
          />
          <AppOverviewFrameworksPopover
            isOpen={isOpen}
            isMerged={isMerged}
            listItems={listItems}
            popoverProps={popoverProps}
          />
        </>
      )}
    </Stack>
  )
}
